// PWA 설치 · 영구 저장 — 「받아 둔 것이 안 지워지게, 그리고 홈에서 바로 열리게」
//
// [왜 한 파일인가] 둘은 같은 목적의 앞뒤다. 설치한 기기는 브라우저가 저장을 **자동으로
//   영구화**하고, 안 한 기기는 디스크가 빠듯할 때 캐시가 통째로 버려진다(지형 메시 캐시가
//   제일 먼저 날아간다). 그래서 설치 안내와 `storage.persist()` 를 같은 자리에서 다룬다.
//
// [계약] `init()` 은 앱이 뜰 때 **한 번**만 부른다. `subscribe(cb)` 는 설치 가능 여부가
//   바뀔 때 알려 주고, 구독을 끊는 함수를 돌려준다. `install()` 은 사용자의 «클릭»
//   안에서만 통한다 — 브라우저가 그 밖의 호출을 무시한다.

const S = { deferred: null, canInstall: false, installed: false, persisted: null, quota: null };

const subs = new Set();
const emit = () => { for (const cb of subs) { try { cb(state()); } catch { /* 구독자 사정 */ } } };

export const state = () => ({ ...S });
export function subscribe(cb) { subs.add(cb); cb(state()); return () => subs.delete(cb); }

/** 이미 설치돼 열린 판인가(홈 화면·앱 창). 그때는 안내를 띄우지 않는다. */
export const isStandalone = () => {
  try {
    return window.matchMedia('(display-mode: standalone)').matches
      || window.matchMedia('(display-mode: fullscreen)').matches
      || window.navigator.standalone === true;
  } catch { return false; }
};

/**
 * 저장을 영구로 올려 달라고 청한다. **거절돼도 정상**이다 — 그때는 종전처럼 「버려질 수 있는」
 * 저장이고, 용량 자체는 그대로다. 설치했거나 자주 쓰는 사이트면 브라우저가 스스로 허락한다.
 */
export async function requestPersist() {
  try {
    if (!navigator.storage?.persist) return null;
    S.persisted = await navigator.storage.persisted();
    if (!S.persisted) S.persisted = await navigator.storage.persist();
    if (navigator.storage.estimate) {
      const e = await navigator.storage.estimate();
      S.quota = { quota: e.quota || 0, usage: e.usage || 0 };
    }
    emit();
    return S.persisted;
  } catch { return null; }
}

/** 설치 안내를 띄운다. 사용자의 클릭 안에서 불러야 한다. */
export async function install() {
  const d = S.deferred;
  if (!d) return 'unavailable';
  S.deferred = null; S.canInstall = false; emit();
  try {
    d.prompt();
    const { outcome } = await d.userChoice;
    return outcome;                     // 'accepted' | 'dismissed'
  } catch { return 'error'; }
}

/** 앱 설치는 운영 주소에서만 — 개발·미리보기 주소도 깔리면 같은 게임이 폰에 아이콘 여러 개로 깔린다(주소마다 다른 앱). */
const INSTALL_HOSTS = new Set(['game.sogething.com', 'www.game.sogething.com', 'broke.kr', 'www.broke.kr']);

export function init() {
  if (typeof window === 'undefined') return;
  if (!INSTALL_HOSTS.has(window.location.hostname)) {
    document.querySelector('link[rel="manifest"]')?.remove();
  }
  S.installed = isStandalone();
  // 크롬 계열만 이 이벤트를 준다. iOS 사파리는 «공유 → 홈 화면에 추가»뿐이라
  // `canInstall` 이 영영 false 다 — 안내 문구를 그쪽에 맞춰 가르는 건 UI 의 몫이다.
  window.addEventListener('beforeinstallprompt', (e) => {
    // 크롬 기본 설치 배너는 막지 않는다 — 막으면 우리 배너가 안 뜨는 자리(게임 화면)에서 설치 권유가 사라진다.
    S.deferred = e; S.canInstall = true; emit();
  });
  window.addEventListener('appinstalled', () => {
    S.deferred = null; S.canInstall = false; S.installed = true; emit();
    requestPersist();                   // 설치 직후가 허락이 가장 잘 떨어지는 때다
  });
  requestPersist();
  if (typeof window !== 'undefined') window.__pwa = { state, install, requestPersist };
}
